import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useResponsive, formats, withApi } from 'utils';
import { datadogLogs } from '@datadog/browser-logs';

import { Dialog, DIALOG_TYPES, Table } from '../../../alf-design';

const paymentRowKey = (payment) =>
  payment.id ?? `${payment.datePaid}-${payment.amounPaid}`;

const formatPaymentMethod = (payment) => {
  const type = (payment.paymentType || '').trim();
  const last4 = (payment.last4CheckOrCardNum || '').replace(/\D/g, '');
  const lower = type.toLowerCase();

  if (lower === 'bank transfer' || lower.includes('transfer')) {
    return 'Bank Transfer';
  }
  if (last4 && type) {
    return `${type} ****${last4}`;
  }
  return type || '—';
};

const formatPaymentStatusLabel = (status) => {
  if (status == null || status === '') return '—';
  const s = String(status).trim();
  const lower = s.toLowerCase();
  if (lower === 'active') return 'Paid';
  if (lower === 'submitted') return 'Pending';
  if (lower === 'reversed') return 'Reversed';
  return s;
};

const statusBadgeClassName = (label) => {
  const base =
    "inline-flex max-w-max rounded-[8px] px-[10px] py-[2px] !font-['InterMedium',sans-serif] !text-[12px] !font-normal !leading-normal";

  if (label === 'Failed' || label === 'Declined') {
    return `${base} border border-[#FFC9C9] bg-[#FEF2F2] !text-[#C10007]`;
  }
  if (label === 'Paid') {
    return `${base} border border-[#B9F8CF] bg-[#F0FDF4] !text-[#008236]`;
  }
  return `${base} border border-legacy-hairline bg-legacy-bgLight !text-legacy-textBody`;
};

const PaymentHistory = ({ show, onClose, api, policyNumber }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const sortDataByDate = (array) => {
    return array.sort((a, b) =>
      moment(a.datePaid).isBefore(b.datePaid) ? 1 : -1,
    );
  };

  const loadPayments = () => {
    setLoading(true);
    api.withAuth
      .getPayments(policyNumber)
      .then((res) => {
        if (res.data) {
          setData(sortDataByDate(res.data.payments));
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        let description;
        if (error.response) {
          description = error.response.data.error;
        } else if (error.request) {
          description =
            'Network error. Please check your connection and try again.';
        } else {
          description = `Error attempting to fetch your payments. Refresh page to try again.`;
        }

        datadogLogs.logger.error(`Payment History - ${policyNumber}`, {
          error,
          description,
        });
      });
  };

  const { height, screenIsDown } = useResponsive();
  const tableMaxHeight = screenIsDown('md') ? '100%' : height / 2;

  useEffect(() => {
    if (show) {
      loadPayments();
    }
  }, [show]);

  const borderedPanelClass =
    'overflow-hidden rounded-[10px] border border-legacy-hairline font-inter';

  const paymentColumns = useMemo(
    () => [
      {
        key: 'datePaid',
        title: 'Date',
        render: (row) => formats.dateFormat(row.datePaid),
      },
      {
        key: 'amounPaid',
        title: 'Amount',
        render: (row) => formats.moneyFormat(row.amounPaid),
      },
      {
        key: 'paymentMethod',
        title: 'Payment Method',
        render: (row) => formatPaymentMethod(row),
      },
      {
        key: 'status',
        title: 'Status',
        render: (row) => {
          const statusLabel = formatPaymentStatusLabel(row.status);
          return (
            <span className={statusBadgeClassName(statusLabel)}>
              {statusLabel}
            </span>
          );
        },
      },
    ],
    [],
  );

  return (
    <Dialog
      id="payment-history"
      type={DIALOG_TYPES.CONTENT}
      invokingElementId="payment-history-button"
      show={show}
      onClose={onClose}
      title="Payment History"
      subtitle="View your payment history and status."
      ariaLabel="Payment history"
    >
      <div className="flex flex-col gap-[16px]">
        <Table
          columns={paymentColumns}
          data={data}
          loading={loading}
          dataRowId={paymentRowKey}
          disableSort
          payment
          maxHeight={tableMaxHeight}
          loadingSpinnerId="payment-history-spinner"
        />

        <div className={`${borderedPanelClass} p-[16px]`}>
          <p className="!m-0 !leading-normal !text-[14px] !text-[#101828]">
            <span className="!font-['InterMedium',sans-serif] !text-[14px] !font-normal">
              Need help with a payment?
            </span>
            <span className="!font-inter !text-[14px] !font-normal">
              {' '}
              Contact our customer service team at{' '}
            </span>
            <a
              className="!font-['InterMedium',sans-serif] !text-[14px] !font-normal text-legacy-primary underline cursor-pointer hover:opacity-90 focus:outline-none focus-visible:underline"
              href="tel:+18007482030"
            >
              (800) 748-2030
            </a>
            <span className="!font-inter !text-[14px] !font-normal">
              {' '}
              or email{' '}
            </span>
            <a
              className="!font-['InterMedium',sans-serif] !text-[14px] !font-normal text-legacy-primary underline cursor-pointer hover:opacity-90 focus:outline-none focus-visible:underline"
              href="mailto:policyservices@slideinsurance.com"
            >
              policyservices@slideinsurance.com
            </a>
          </p>
        </div>
      </div>
    </Dialog>
  );
};

PaymentHistory.propTypes = {
  policyNumber: PropTypes.string,
  show: PropTypes.bool,
  onClose: PropTypes.func,
};

export default withApi(PaymentHistory);
